import { useState } from "react";
import { useCreditPricing } from "hooks/useCreditPricing";
import { CREDIT_PURCHASE_STEP } from "utils/pricing";

interface CreditAmountFieldProps {
  id: string;
  value: number;
  onChange: (credits: number) => void;
  onDraftChange?: (credits: number | null) => void;
  className?: string;
}

/**
 * Editable credit amount, paired with the slider.
 *
 * Typing is the precise path — a buyer who has worked out they need 340 credits
 * shouldn't have to hunt for it on a track. The field holds raw text while
 * focused so intermediate states like "3" or "" are typeable, but the amount it
 * reports is live: the slider, price and capacity hints answer every keystroke.
 */
const CreditAmountField = ({
  id,
  value,
  onChange,
  onDraftChange,
  className = "",
}: CreditAmountFieldProps) => {
  const { pricing } = useCreditPricing();

  const digitsOf = (text: string) => text.replace(/[^\d]/g, "");

  const clampToRange = (credits: number) =>
    Math.min(pricing.maxPurchase, Math.max(pricing.minPurchase, credits));

  // Snap onto the server's purchasable range, on a whole step.
  const normalize = (credits: number) => {
    if (!Number.isFinite(credits)) return pricing.minPurchase;
    return clampToRange(Math.round(credits / CREDIT_PURCHASE_STEP) * CREDIT_PURCHASE_STEP);
  };

  // `null` means "not editing" — the field mirrors the slider. Any string means
  // the user is typing and owns the text until they commit.
  const [draft, setDraft] = useState<string | null>(null);

  /**
   * Typing drives the slider on every keystroke, so the field is the control
   * rather than a readout that catches up on blur. Only the range is enforced
   * here — step snapping waits for commit, so a half-typed "57" isn't rounded
   * out from under the caret.
   */
  const handleChange = (text: string) => {
    setDraft(text);
    const digits = digitsOf(text);
    onDraftChange?.(digits === "" ? null : Number(digits));
    // Nothing to sync to yet (cleared, or mid-retype) — hold the last amount.
    if (digits === "") return;
    onChange(clampToRange(Number(digits)));
  };

  const commit = () => {
    if (draft === null) return;
    const digits = digitsOf(draft);
    onChange(normalize(digits === "" ? pricing.minPurchase : Number(digits)));
    setDraft(null);
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label="Credits to purchase"
      value={draft ?? value.toLocaleString()}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className={`w-full h-[50px] pl-2.5 pr-4 border border-[rgba(1,33,105,0.5)] rounded-[8px] text-[#101828] bg-white tabular-nums focus:outline-none focus:border-[#012169] ${className}`}
      title={`Between ${pricing.minPurchase.toLocaleString()} and ${pricing.maxPurchase.toLocaleString()} credits`}
    />
  );
};

export { CreditAmountField };
